import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar,
  Clock,
  RefreshCw,
  Tag,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdPlacement } from "@/components/ads/AdPlacement";

import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { useBlogPost } from "@/tools/blog/hooks/use-blog-data";
import { BlogBreadcrumb } from "../components/BlogBreadcrumb";
import { BlogCover } from "../components/BlogCover";
import { BlogPostContent } from "../components/BlogPostContent";
import { BlogTOC } from "../components/BlogTOC";

interface TocEntry {
  id: string;
  text: string;
  level: number;
}

export function BlogPost({
  tool,
  topic,
  slug,
}: {
  tool: string;
  topic: string;
  slug: string;
}) {
  const locale = getLocale();
  const { data: post, isLoading } = useBlogPost(tool, topic, slug, locale);
  const [readProgress, setReadProgress] = useState(0);

  const tocEntries = useMemo<TocEntry[]>(() => {
    if (!post?.toc) return [];
    try {
      return JSON.parse(post.toc) as TocEntry[];
    } catch {
      return [];
    }
  }, [post?.toc]);

  // Reading progress bar
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (isLoading) {
    return (
      <div className="relative">
        <div className="pointer-events-none absolute inset-0 gradient-mesh" />
        <div className="relative mx-auto max-w-4xl px-4 py-12">
          <div className="h-6 w-48 animate-pulse rounded bg-muted" />
          <div className="mt-8 h-12 w-full animate-pulse rounded bg-muted" />
          <div className="mt-4 h-4 w-64 animate-pulse rounded bg-muted" />
          <div className="mt-12 space-y-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="h-4 animate-pulse rounded bg-muted"
                style={{ width: `${70 + Math.random() * 30}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <h1 className="font-display text-2xl font-bold">
          {m.blog_post_not_found()}
        </h1>
        <Link
          to="/blog"
          className="mt-4 flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {m.tool_blog_name()}
        </Link>
      </div>
    );
  }

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  const publishedDate = post.publishedAt ? fmt(post.publishedAt) : null;
  // `updatedAt` is the content-gated modification date (see blog-publish.ts),
  // so "Updated" only appears when the article genuinely changed after publish.
  const updatedDate =
    post.updatedAt &&
    post.publishedAt &&
    post.updatedAt.slice(0, 10) > post.publishedAt.slice(0, 10)
      ? fmt(post.updatedAt)
      : null;

  return (
    <div className="relative">
      {/* Reading progress bar */}
      <div className="fixed left-0 right-0 top-0 z-50 h-0.5">
        <div
          className="h-full bg-gradient-to-r from-primary via-accent to-secondary transition-[width] duration-150"
          style={{ width: `${readProgress * 100}%` }}
        />
      </div>

      {/* Atmospheric background */}
      <div className="pointer-events-none absolute inset-0 gradient-mesh" />

      <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="animate-slide-up">
          <BlogBreadcrumb
            items={[
              { label: m.tool_blog_name(), to: "/blog" },
              {
                label: post.toolName,
                to: "/blog/$tool",
                params: { tool },
              },
              {
                label: post.topicName,
                to: "/blog/$tool/$topic",
                params: { tool, topic },
              },
              { label: post.title },
            ]}
          />
        </div>

        {/* Article header */}
        <header className="animate-slide-up delay-100 mx-auto mt-8 max-w-3xl text-center opacity-0">
          {/* Topic eyebrow — motorsport gold */}
          <span className="mb-4 inline-flex items-center justify-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-secondary">
            <span className="h-1.5 w-1.5 rounded-full bg-secondary shadow-[0_0_6px_oklch(0.78_0.16_75/0.8)]" />
            {post.topicName}
          </span>

          {/* Title */}
          <h1 className="font-display mt-3 text-3xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>

          {/* Meta info — timing-screen readout */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 font-mono text-[13px] text-muted-foreground">
            {/* Byline — a named, consistent author identity (E-E-A-T) */}
            <Link
              to="/about"
              className="flex items-center gap-1.5 transition-colors hover:text-foreground"
              rel="author"
            >
              <Users className="h-3.5 w-3.5" />
              {m.blog_by_author({ author: "Toolverse Team" })}
            </Link>
            <span className="h-0.5 w-0.5 rounded-full bg-muted-foreground/40" />
            {publishedDate && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <time dateTime={post.publishedAt ?? undefined}>
                  {publishedDate}
                </time>
              </span>
            )}
            {updatedDate && (
              <>
                <span className="h-0.5 w-0.5 rounded-full bg-muted-foreground/40" />
                <span className="flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5" />
                  <time dateTime={post.updatedAt ?? undefined}>
                    {m.blog_updated_on({ date: updatedDate })}
                  </time>
                </span>
              </>
            )}
            <span className="h-0.5 w-0.5 rounded-full bg-muted-foreground/40" />
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {post.readingTimeMinutes} {m.blog_minutes_read()}
            </span>
          </div>

          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag.slug}
                  className="inline-flex items-center gap-1 rounded-full border border-border/50 bg-muted/50 px-3 py-1 text-xs text-muted-foreground"
                >
                  <Tag className="h-2.5 w-2.5" />
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Cover image — framed app-window capture */}
        {post.coverImage && (
          <div className="animate-slide-up delay-200 mx-auto mt-10 max-w-4xl opacity-0">
            <BlogCover
              coverImage={post.coverImage}
              coverImageMobile={post.coverImageMobile}
              alt={post.title}
              size="hero"
              priority
              className="aspect-video w-full rounded-2xl border border-border shadow-[0_24px_48px_-24px_oklch(0.2_0.03_265/0.35)] dark:border-white/[0.08] dark:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)]"
            />
          </div>
        )}

        {/* Divider line */}
        <div className="mx-auto mt-10 max-w-3xl">
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        </div>

        {/* 2-column layout: content + sidebar */}
        <div className="animate-slide-up delay-300 mt-10 grid grid-cols-[minmax(0,1fr)] gap-10 opacity-0 lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-12">
          {/* Main content — elevated reading surface */}
          <article className="mx-auto w-full min-w-0 max-w-3xl lg:mx-0">
            <div className="rounded-2xl border border-border bg-card px-6 py-10 shadow-[0_1px_2px_oklch(0.2_0.03_265/0.06),0_8px_32px_-12px_oklch(0.2_0.03_265/0.14)] sm:px-10 sm:py-12 lg:px-14 lg:py-14 dark:border-white/[0.08] dark:bg-gradient-to-b dark:from-[rgba(30,34,45,0.95)] dark:to-[rgba(25,28,38,0.9)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.04),inset_0_1px_0_rgba(255,255,255,0.06)]">
              <BlogPostContent html={post.html} chartData={post.chartData} />
            </div>
            <AdPlacement className="mt-8" />
          </article>

          {/* Sidebar */}
          {tocEntries.length > 0 && (
            <aside className="hidden lg:block">
              <BlogTOC entries={tocEntries} />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
